import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Collapse,
  IconButton,
  useMediaQuery,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import { MatxLayoutSettings } from "app/components/MatxLayout/settings";
import { getSoldCount } from "../utils/dashboardUtils";
import TableComponent from "app/components/commonComponents/TableComponent";

const ProductItem = ({ product, unitLabel, gridSize }) => {
  const isMobile = useMediaQuery("(max-width:768px)");

  const totalSold = product.categories.reduce(
    (acc, cat) =>
      acc +
      cat.variations.reduce(
        (vAcc, variation) => vAcc + getSoldCount(variation),
        0
      ),
    0
  );

  const isHotel = product.categories?.some(
    (category) => category.category_name === "Hotel"
  );

  const [expanded, setExpanded] = useState(isHotel);

  // Responsive Hotel Columns
  const hotelColumns = isMobile
  ? [
      {
        label: "Room Type",
        field: "variation_name",
        width: "50%",
      },
      {
        label: "Details",
        field: "summary",
        width: "50%",
      },
    ]
  : [
      {
        label: "Room Type",
        field: "variation_name",
        width: 150,
      },
      {
        label: "Total",
        field: "total_quantity",
        minWidth: 60,
      },
      {
        label: "Sold",
        field: "sold",
        minWidth: 60,
      },
      {
        label: "Available",
        field: "available_quantity",
        minWidth: 60,
      },
    ];

  const hotelRows = product.categories.flatMap((category) =>
  category.variations.map((variation) => ({
    variation_name: isMobile ? (
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          whiteSpace: "normal",
          wordBreak: "break-word",
          overflowWrap: "break-word",
          hyphens: "auto",
          lineHeight: 1.5,
          fontSize: "0.82rem",
        }}
      >
        {variation.variation_name}
      </Box>
    ) : (
      variation.variation_name
    ),

    sold: getSoldCount(variation),
    available_quantity: variation.available_quantity,
    total_quantity: variation.total_quantity,

    summary: isMobile ? (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.4,
          width: "100%",
          whiteSpace: "normal",
        }}
      >
        <Typography variant="caption">
          <strong>Total:</strong> {variation.total_quantity}
        </Typography>

        <Typography variant="caption">
          <strong>Sold:</strong> {getSoldCount(variation)}
        </Typography>

        <Typography variant="caption">
          <strong>Available:</strong>{" "}
          {variation.available_quantity}
        </Typography>
      </Box>
    ) : null,
  }))
);

  return (
    <Grid item {...gridSize}>
      <Box
        sx={{
          borderLeft: `3px solid ${MatxLayoutSettings.palette.active}`,
          pl: 2.5,
          py: 0.5,
        }}
      >
        <Box
          onClick={() => !isHotel && setExpanded(!expanded)}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            cursor: "pointer",
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="subtitle1"
              fontWeight="700"
              sx={{
                color: MatxLayoutSettings.palette.black,
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {product.product_name}
            </Typography>

            <Typography
              variant="caption"
              color={MatxLayoutSettings.palette.textBlack}
              fontWeight="600"
              sx={{
                mb: 1,
                display: "block",
              }}
            >
              {isHotel ? (
                <>
                  Total Rooms: {product.product_total_quantity}
                  <br />
                  Total Sold: {totalSold}
                  <br />
                  Total Availability: {product?.product_available_quantity}
                </>
              ) : (
                <>
                  {totalSold} / {product.product_total_quantity} {unitLabel}
                </>
              )}
            </Typography>
          </Box>

          {!isHotel && (
            <IconButton
              size="small"
              sx={{
                mt: -0.5,
                flexShrink: 0,
                pointerEvents: "none",
              }}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </Box>

        {isHotel ? (
          <Box
            sx={{
              mt: 2,
              width: "100%",
              overflowX: "hidden",
            }}
          >
            <TableComponent
              loading={false}
              columns={hotelColumns}
              rows={hotelRows}
              noDataMessage="No room data available"
              sx={{
                width: "100%",
                tableLayout: "fixed",
                boxShadow: "none",
                border: `1px solid ${MatxLayoutSettings.palette.borderGrey}`,

                "& td": {
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                },
              }}
            />
          </Box>
        ) : (
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box
              display="flex"
              flexDirection="column"
              gap={1.5}
              sx={{ mt: 1.5 }}
            >
              {product.categories.map((category) =>
                category.variations.map((variation) => (
                  <Box
                    key={variation.variation_id}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      minWidth: 0,
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        color: MatxLayoutSettings.palette.black,
                        fontWeight: 500,
                        flex: 1,
                      }}
                    >
                      {variation.variation_name}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: MatxLayoutSettings.palette.black,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {getSoldCount(variation)} /{" "}
                      {variation.total_quantity}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          </Collapse>
        )}
      </Box>
    </Grid>
  );
};

const ProductBreakdown = ({
  title,
  description,
  products,
  unitLabel = "sold",
}) => {
  if (!products?.length) return null;

  const count = products.length;

 const gridSize = {
  xs: 12,   
  sm: 12,  
  md: 6,   
  lg: 6,    
};

  return (
    <Card
      sx={{
        borderRadius: "12px",
        border: `1px solid ${MatxLayoutSettings.palette.borderGrey}`,
        mt: 4,
        boxShadow: "none",
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h6" fontWeight="700">
          {title}
        </Typography>

        {description && (
          <Typography
            variant="body2"
            color={MatxLayoutSettings.palette.textBlack}
            sx={{ mb: 4 }}
          >
            {description}
          </Typography>
        )}

        <Grid container spacing={4}>
          {products.map((product) => (
            <ProductItem
              key={product.product_id}
              product={product}
              unitLabel={unitLabel}
              gridSize={gridSize}
            />
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default ProductBreakdown;